import { FC, SyntheticEvent, useContext, useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Divider, Link, Stack, TextField, useTheme } from '@mui/material';
import API from '../../api/api';
import GlobalContext from '../../contexts/GlobalContext';
import LoginContext, { ELoginUiState } from '../../contexts/LoginContext';
import { IconApple, IconFacebook, IconGoogle, MBLogo } from '../../icons/Icons';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import CoBrandingV2 from '../CoBrandingV2/CoBrandingV2';
import styles from './LoginConsumerEmail.module.scss';
import { EBrowserOS, ELDFlags, ELinks, EMixpanelEvent } from '../../util/constants';
import ThirdPartyLogin from '../ThirdPartyLogin/ThirdPartyLogin';
import mixpanel from 'mixpanel-browser';
import { useFlags } from 'launchdarkly-react-client-sdk';

const LoginConsumerEmail: FC = () => {
    const { browser, coBranding, language, staffLoginLink, subscriberId, returnUrlParameter } =
        useContext(GlobalContext);
    const {
        loginUiState,
        setLoginUiState,
        username,
        setUsername,
        usernameIsValid,
        usernameValidationMessage,
        findConsumerUser,
        loginErrorMessage,
        checkAzureAdRedirect,
        isCreateAccountRequest,
    } = useContext(LoginContext);
    const { [ELDFlags.deprecateFacebook]: isFacebookDeprecated, [ELDFlags.removeFacebook]: isFacebookRemoved } =
        useFlags();
    const [isLoading, setIsLoading] = useState(false);
    const theme = useTheme();
    const showStaffLoginLink = useMemo(() => {
        if (subscriberId && browser.OS !== EBrowserOS.iOS) {
            return true;
        }
        return false;
    }, [browser, subscriberId]);

    const handleSubmitEmail = async (event: SyntheticEvent) => {
        event.preventDefault();
        setIsLoading(true);
        const shouldRedirectToAzureAd = checkAzureAdRedirect();
        if (!shouldRedirectToAzureAd) {
            await findConsumerUser();
            setIsLoading(false);
        }
    };

    const handleFacebookLogin = () => {
        if (isFacebookDeprecated) {
            setLoginUiState(ELoginUiState.deprecateFacebook);
        } else {
            const facebookLoginLink = returnUrlParameter
                ? `${ELinks.loginFacebook}&${returnUrlParameter}`
                : `${ELinks.loginFacebook}`;
            API.post.trackEvent({ event: 'Sign In || Consumer Sign in | Button - Sign in with Facebook' });
            mixpanel.track(EMixpanelEvent.actionStart, { key: 'consumer_signin' });
            mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_signin_facebook' });
            window.location.assign(facebookLoginLink);
        }
    };
    const handleGoogleLogin = () => {
        const googleLoginLink = returnUrlParameter
            ? `${ELinks.loginGoogle}&${returnUrlParameter}`
            : `${ELinks.loginGoogle}`;
        API.post.trackEvent({ event: 'Sign In || Consumer Sign in | Button - Sign in with Google' });
        mixpanel.track(EMixpanelEvent.actionStart, { key: 'consumer_signin' });
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_signin_google' });
        window.location.assign(googleLoginLink);
    };
    const handleAppleLogin = () => {
        const appleLoginLink = returnUrlParameter
            ? `${ELinks.loginApple}&${returnUrlParameter}`
            : `${ELinks.loginApple}`;
        API.post.trackEvent({ event: 'Sign In || Consumer Sign in | Button - Sign in with Apple' });
        mixpanel.track(EMixpanelEvent.actionStart, { key: 'consumer_signin' });
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_signin_apple' });
        window.location.assign(appleLoginLink);
    };

    useEffect(() => {
        if (loginUiState === ELoginUiState.ready) {
            mixpanel.track(EMixpanelEvent.viewed, { key: 'consumer_signin_start' });
        }
        if (loginUiState !== ELoginUiState.ready) {
            setLoginUiState(ELoginUiState.ready);
        }
    }, [loginUiState]);

    return (
        <PageLayoutV2 testId="login-consumer-email">
            {coBranding.logoUrl || coBranding.name ? (
                <Stack>
                    <CoBrandingV2 logoUrl={coBranding.logoUrl} name={coBranding.name} />
                    <div className={styles.mbLineBreak}>
                        <span>
                            {isCreateAccountRequest ? language.login.createAccountWith : language.login.continueWith}
                        </span>
                        <i>
                            <MBLogo width={'100%'} height={'100%'} />
                        </i>
                    </div>
                </Stack>
            ) : (
                <section className={styles.mbLogo} data-testid="mb-logo">
                    <span>
                        <MBLogo width={'100%'} height={'100%'} />
                    </span>
                </section>
            )}
            {loginUiState === ELoginUiState.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {loginErrorMessage}
                </Alert>
            )}
            <form>
                <Stack spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        label={language.login.username.consumer}
                        value={username}
                        onChange={(event) => setUsername(event.target.value)}
                        error={!usernameIsValid}
                        helperText={!usernameIsValid && usernameValidationMessage}
                        autoFocus
                        fullWidth
                        id="username"
                        size="small"
                        className={styles.email}
                    />
                    <LoadingButton
                        disabled={isLoading}
                        onClick={handleSubmitEmail}
                        loading={isLoading}
                        type="submit"
                        variant="contained"
                        size="large"
                    >
                        {language.login.checkEmailCTA}
                    </LoadingButton>
                </Stack>
            </form>
            <Divider sx={{ my: 3, typography: 'body2' }}>{language.common.or}</Divider>
            <Stack direction={'row'} spacing={2} sx={{ justifyContent: 'center', my: 2 }}>
                <ThirdPartyLogin
                    label={language.login.loginGoogleCTA}
                    icon={<IconGoogle />}
                    onClick={handleGoogleLogin}
                />
                <ThirdPartyLogin label={language.login.loginAppleCTA} icon={<IconApple />} onClick={handleAppleLogin} />
                {!isFacebookDeprecated && (
                    <ThirdPartyLogin
                        label={language.login.loginFacebookCTA}
                        icon={<IconFacebook />}
                        onClick={handleFacebookLogin}
                    />
                )}
            </Stack>
            {/* Only display staff login link if in a subscriber context */}
            {(showStaffLoginLink || isFacebookDeprecated) && (
                <Stack direction={'row'} sx={{ justifyContent: 'center', my: 4 }}>
                    {showStaffLoginLink && (
                        <Link
                            onClick={() => {
                                mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_signin_staff' });
                            }}
                            href={staffLoginLink}
                            sx={{ typography: 'body2' }}
                            color={theme.palette.text.primary}
                            data-testid="staff-login-link"
                        >
                            {language.login.notConsumer.cta}
                        </Link>
                    )}
                    {showStaffLoginLink && isFacebookDeprecated && !isFacebookRemoved && (
                        <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                    )}
                    {isFacebookDeprecated && !isFacebookRemoved && (
                        <Link
                            component={'button'}
                            onClick={handleFacebookLogin}
                            sx={{ typography: 'body2' }}
                            color={theme.palette.text.primary}
                            data-testid="facebook-help"
                        >
                            {language.login.loginFacebookHelp}
                        </Link>
                    )}
                </Stack>
            )}
        </PageLayoutV2>
    );
};

export default LoginConsumerEmail;
